import { useLocation } from 'react-router-dom';
import { Link as RouterLink } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Tooltip,
  useTheme,
  Avatar,
  Typography,
  Fade,
  Button,
} from '@mui/material';
import { ClayChip } from './ClayChip';
import SchoolIcon from '@mui/icons-material/School';
import NoteIcon from '@mui/icons-material/Note';
import HistoryIcon from '@mui/icons-material/History';
import SettingsIcon from '@mui/icons-material/Settings';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EventNoteIcon from '@mui/icons-material/EventNote';
import HomeIcon from '@mui/icons-material/Home';
import HelpIcon from '@mui/icons-material/Help';
import BugReportIcon from '@mui/icons-material/BugReport';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import BugReportModal from './BugReportModal';

interface SidebarProps {
  onWidthChange: (width: number) => void;
}

export default function Sidebar({ onWidthChange }: SidebarProps) {
  const location = useLocation();
  const theme = useTheme();
  const { user } = useAuth();
  const { settings } = useSettings();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [bugReportOpen, setBugReportOpen] = useState(false);
  const { isPremium } = useSubscription();

  const menuItems = [
    { text: 'Home', icon: <HomeIcon />, path: '/dashboard' },
    { text: 'Study', icon: <SchoolIcon />, path: '/study-setup' },
    { text: 'AI Study', icon: <AutoAwesomeIcon />, path: '/ai-study' },
    { text: 'Upcoming Tests', icon: <EventNoteIcon />, path: '/upcoming-tests' },
    { text: 'Notes', icon: <NoteIcon />, path: '/notes' },
    { text: 'History', icon: <HistoryIcon />, path: '/history' },
  ];

  const bottomMenuItems = [
    { text: 'Settings', icon: <SettingsIcon />, path: '/settings' },
    { text: 'FAQ', icon: <HelpIcon />, path: '/app/faq' },
    { text: 'Billing', icon: <CreditCardIcon />, path: '/billing' },
    { 
      text: 'Report a Bug', 
      icon: <BugReportIcon />, 
      onClick: () => setBugReportOpen(true),
      isAction: true 
    },
  ];

  const sidebarWidth = isCollapsed ? 64 : 240;

  useEffect(() => {
    onWidthChange(sidebarWidth);
  }, [sidebarWidth, onWidthChange]);

  return (
    <>
      <Box
        component="nav"
        sx={{
          width: sidebarWidth,
          flexShrink: 0,
          borderRight: `2px solid ${alpha(theme.palette.primary.main, 0.40)}`,
          height: 'calc(100vh - 64px)', // Subtract navbar height
          position: 'fixed',
          left: 0,
          top: 64, // Height of navbar
          background: `linear-gradient(180deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
          display: 'flex',
          flexDirection: 'column',
          transition: theme.transitions.create(['width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        }}
      >
        <Tooltip title={isCollapsed ? "Expand" : "Collapse"}>
          <IconButton
            onClick={() => setIsCollapsed(!isCollapsed)}
            size="small"
            sx={{
              position: 'absolute',
              right: -16,
              top: '50%',
              transform: 'translateY(-50%)',
              background: theme.palette.mode === 'dark'
                ? `linear-gradient(145deg, ${theme.palette.grey[700]}, ${theme.palette.grey[800]})`
                : `linear-gradient(145deg, ${theme.palette.background.paper}, ${theme.palette.grey[100]})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.40)}`,
              boxShadow: [
                '0 3px 10px rgba(0,0,0,0.12)',
                'inset 0 1px 0 rgba(255,255,255,0.85)',
                'inset 0 -1px 0 rgba(0,0,0,0.06)',
              ].join(', '),
              width: 32,
              height: 32,
              transition: 'transform 0.16s ease, box-shadow 0.16s ease',
              '&:hover': {
                background: theme.palette.mode === 'dark'
                  ? `linear-gradient(145deg, ${theme.palette.grey[600]}, ${theme.palette.grey[700]})`
                  : `linear-gradient(145deg, ${theme.palette.common.white}, ${theme.palette.grey[50]})`,
                transform: 'translateY(-50%) scale(1.1)',
                boxShadow: [
                  '0 5px 14px rgba(0,0,0,0.16)',
                  'inset 0 1px 0 rgba(255,255,255,0.9)',
                  'inset 0 -1px 0 rgba(0,0,0,0.07)',
                ].join(', '),
              },
              '&:active': {
                transform: 'translateY(-50%) scale(0.95)',
              },
              zIndex: 1,
            }}
          >
            {isCollapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
          </IconButton>
        </Tooltip>

        {/* User Profile Section */}
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            sx={{
              width: 40,
              height: 40,
              bgcolor: 'primary.main',
              boxShadow: [
                `0 4px 14px ${alpha(theme.palette.primary.main, 0.45)}`,
                '0 2px 6px rgba(0,0,0,0.12)',
                'inset 0 1.5px 0 rgba(255,255,255,0.3)',
                'inset 0 -1.5px 0 rgba(0,0,0,0.15)',
              ].join(', '),
            }}
          >
            {user?.email?.[0].toUpperCase()}
          </Avatar>
          {!isCollapsed && (
            <Fade in={!isCollapsed}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {user?.email}
                </Typography>
                {isPremium && (
                  <ClayChip
                    label="Pro"
                    color="primary"
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                )}
                {settings.betaFeatures && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <LocalFireDepartmentIcon sx={{ color: 'warning.main', fontSize: 16 }} />
                    <Typography variant="caption" color="text.secondary">
                      7 day streak
                    </Typography>
                  </Box>
                )}
              </Box>
            </Fade>
          )}
        </Box>

        {!isPremium && !isCollapsed && (
          <Box sx={{ px: 2, pb: 2 }}>
            <Button
              component={RouterLink}
              to="/app/pricing"
              variant="contained"
              fullWidth
              size="small"
              sx={{
                borderRadius: 2.5,
                textTransform: 'none',
                fontWeight: 700,
                background: `linear-gradient(145deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
                transition: 'transform 0.16s ease, box-shadow 0.16s ease',
                boxShadow: [
                  `0 4px 14px ${alpha(theme.palette.primary.main, 0.42)}`,
                  '0 2px 5px rgba(0,0,0,0.1)',
                  'inset 0 1.5px 0 rgba(255,255,255,0.28)',
                  `inset 0 -1.5px 0 ${alpha(theme.palette.primary.dark, 0.25)}`,
                ].join(', '),
                '&:hover': {
                  background: `linear-gradient(145deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  transform: 'translateY(-1px) scale(1.01)',
                  boxShadow: [
                    `0 6px 18px ${alpha(theme.palette.primary.main, 0.52)}`,
                    '0 3px 8px rgba(0,0,0,0.12)',
                    'inset 0 1.5px 0 rgba(255,255,255,0.28)',
                    `inset 0 -1.5px 0 ${alpha(theme.palette.primary.dark, 0.3)}`,
                  ].join(', '),
                },
                '&:active': {
                  transform: 'translateY(1px) scale(0.99)',
                },
              }}
            >
              Upgrade
            </Button>
          </Box>
        )}

        <Divider sx={{ borderColor: alpha(theme.palette.primary.main, 0.30) }} />

        {/* Main Menu Items */}
        <List sx={{ pt: 1, flex: 1 }}>
          {menuItems.map((item) => {
            const active = location.pathname === item.path;
            return (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                component={RouterLink}
                to={item.path}
                selected={active}
                sx={{
                  minHeight: 48,
                  px: isCollapsed ? 0 : 2.5,
                  mx: isCollapsed ? 0 : 1,
                  borderRadius: 2,
                  justifyContent: isCollapsed ? 'center' : 'flex-start',
                  '&.Mui-selected': {
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.14)}, ${alpha(theme.palette.primary.light, 0.08)})`,
                    boxShadow: [
                      `0 2px 10px ${alpha(theme.palette.primary.main, 0.15)}`,
                      'inset 0 1px 0 rgba(255,255,255,0.6)',
                      `inset 0 -1px 0 ${alpha(theme.palette.primary.main, 0.1)}`,
                    ].join(', '),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.18)}, ${alpha(theme.palette.primary.light, 0.12)})`,
                    },
                    '& .MuiListItemIcon-root': {
                      color: 'primary.main',
                    },
                  },
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                  transition: 'background 0.18s ease, box-shadow 0.18s ease',
                }}
              >
                <ListItemIcon 
                  sx={{ 
                    minWidth: isCollapsed ? 'auto' : 0,
                    mr: isCollapsed ? 0 : 3,
                    justifyContent: 'center',
                    color: location.pathname === item.path ? 'primary.main' : 'inherit',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {!isCollapsed && (
                  <ListItemText
                    primary={item.text}
                    sx={{
                      color: location.pathname === item.path ? 'primary.main' : 'text.primary',
                      opacity: 1,
                      transition: theme.transitions.create(['opacity'], {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.enteringScreen,
                      }),
                    }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          );
          })}
        </List>

        <Divider sx={{ borderColor: alpha(theme.palette.primary.main, 0.30) }} />

        {/* Bottom Menu Items */}
        <List>
          {bottomMenuItems.map((item) => {
            const active = !item.isAction && location.pathname === item.path;
            return (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                component={item.isAction ? 'div' : RouterLink}
                to={item.path}
                onClick={item.onClick}
                selected={active}
                sx={{
                  minHeight: 48,
                  px: isCollapsed ? 0 : 2.5,
                  mx: isCollapsed ? 0 : 1,
                  borderRadius: 2,
                  justifyContent: isCollapsed ? 'center' : 'flex-start',
                  '&.Mui-selected': {
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.14)}, ${alpha(theme.palette.primary.light, 0.08)})`,
                    boxShadow: [
                      `0 2px 10px ${alpha(theme.palette.primary.main, 0.15)}`,
                      'inset 0 1px 0 rgba(255,255,255,0.6)',
                      `inset 0 -1px 0 ${alpha(theme.palette.primary.main, 0.1)}`,
                    ].join(', '),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.18)}, ${alpha(theme.palette.primary.light, 0.12)})`,
                    },
                    '& .MuiListItemIcon-root': {
                      color: 'primary.main',
                    },
                  },
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                  transition: 'background 0.18s ease, box-shadow 0.18s ease',
                }}
              >
                <ListItemIcon 
                  sx={{ 
                    minWidth: isCollapsed ? 'auto' : 0,
                    mr: isCollapsed ? 0 : 3,
                    justifyContent: 'center',
                    color: !item.isAction && location.pathname === item.path ? 'primary.main' : 'inherit',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {!isCollapsed && (
                  <ListItemText
                    primary={item.text}
                    sx={{
                      color: !item.isAction && location.pathname === item.path ? 'primary.main' : 'text.primary',
                      opacity: 1,
                      transition: theme.transitions.create(['opacity'], {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.enteringScreen,
                      }),
                    }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          );
          })}
        </List>
      </Box>

      <BugReportModal
        open={bugReportOpen}
        onClose={() => setBugReportOpen(false)}
        userEmail={user?.email || ''}
      />
    </>
  );
} 